import { useLayoutEffect, useMemo, useRef, useState } from "react";
import Icon, { ICON_NAME } from "../../../components/Icon";
import ScrollVertical from "../../../components/ScrollVertical";
import { datetime } from "../../../util/library/datetime";
import { aMinutesMillisecond, OPERATION_TYPE, SHORT_DATETIME_FORMAT, STEP_KEY_V2 } from "../../../util/varibles/constants";
import { convertStepName, stepNameV2 } from "../../../util/varibles/global";
import { IOperation2, TOperation2 } from "../../../util/varibles/interface";
import styles from "../style.module.scss";
import { getTimelineFromActivityLog } from "./contants";
import { useProcessData } from "./ProcessDataContext";
import TimePoint, { ITimePoint } from "./TimePoint";
import TimePointBg from "./TimePointBg";

interface IProps {
    operation: TOperation2
    activity_log: IOperation2['activity_log']
}

export default function TimelineRange({ operation, activity_log }: IProps) {
    const [startTime, set] = useProcessData(state => state.start);
    const [finishTime] = useProcessData(state => state.finish);
    const [selected, setSelected] = useState(-1);
    const labelContainerRef = useRef<HTMLDivElement>(null);
    const [labelRows, setLabelRows] = useState<number[]>([]);
    const startOp = activity_log[0];
    const finishOp = activity_log[activity_log.length - 1];
    const stepMinutes = 15;

    const labels: ITimePoint[] = useMemo(() => {
        switch (operation.operation_type) {
            case OPERATION_TYPE.HARVEST: {
                const list: ITimePoint[] = activity_log.flatMap(item => {
                    const name = stepNameV2(item.key);
                    const { real_start_time, real_finish_time } = item;
                    if (!real_start_time) return [];

                    if ((item.key === STEP_KEY_V2.HARVESTING || item.key === STEP_KEY_V2.DISCHARGING) && !item.real_sub_steps) {
                        return [];
                    }

                    const value: ITimePoint[] = [{ name, key: item.key, start: real_start_time },];

                    if (real_finish_time) {
                        value.push({
                            name: `Finish ${name.toLowerCase()}`,
                            key: item.key,
                            start: real_finish_time,
                        });
                    }
                    return value;
                });
                const timelineLabels = list[list.length - 1]?.key === STEP_KEY_V2.COMPLETED_HARVEST
                    ? list.slice(0, -1)
                    : list;

                return timelineLabels;
            }
            case OPERATION_TYPE.TREATMENT: {
                const { real_round_map = {} } = operation;
                const group: Record<string, ITimePoint> = {};
                for (let roundId in real_round_map) {
                    const item = real_round_map[roundId];
                    const { start_time, tanks = [] } = item;
                    if (!start_time)
                        continue;
                    tanks.forEach(tank => {
                        const { start_time = 0, finish_time = 0, processes = [], start_places = [] } = tank
                        const finishTank = Math.max(finish_time, processes[processes?.length - 1].finish_time);
                        const key = `round${roundId}_unit${start_places[0]}`;
                        const { steps = {} } = group[key] || {};
                        processes.forEach(step => {
                            const { start_time: startStep, finish_time: finishStep, keys } = step;
                            if (start_time && finish_time) {
                                const stepId = keys.join('|');
                                if (steps[stepId]) {
                                    steps[stepId].start = Math.min(startStep, steps[stepId].start)
                                } else {
                                    steps[stepId] = {
                                        name: convertStepName(keys).join(', '),
                                        start: startStep,
                                        finish: finishStep
                                    }
                                }
                            }
                        })
                        group[key] = {
                            name: `R${roundId}|${start_places[0]}`,
                            start: group[key] ? Math.min(start_time, group[key].start) : start_time,
                            finish: Math.max(finishTank, group[key]?.finish || 0),
                            steps
                        }
                    })
                }
                return Object.keys(group).map(key => group[key]);
            }
            default:
                return []
        }
    }, [operation, activity_log]);

    const data = useMemo(() => {
        if (!Number.isFinite(startTime) || !Number.isFinite(finishTime) || !startTime || !finishTime) return [];

        const endTime = Math.min(finishTime, Date.now());

        const list: number[] = [];
        const stepMilliseconds = stepMinutes * aMinutesMillisecond;
        let current = Math.ceil(startTime / stepMilliseconds) * stepMilliseconds;

        while (current <= endTime) {
            list.push(current);
            current += stepMilliseconds;
        }

        return list;
    }, [startTime, finishTime, stepMinutes]);

    const handleClick = (i: number = -1, value?: ITimePoint,) => {
        if (value) {
            const { start, finish } = value;
            if (start && finish)
                set({ start, finish })
        } else {
            const { start, finish } = getTimelineFromActivityLog()
            set({ start, finish })
        }
        setSelected(i)
    }

    const selectedLabels = selected === -1
        ? labels
        : Object.keys(labels[selected]?.steps || {}).map(key => {
            const item = labels[selected].steps?.[key] || {};
            return item as ITimePoint
        });

    useLayoutEffect(() => {
        const container = labelContainerRef.current;

        if (!container) return;

        const updateLabelRows = () => {
            const labelElements = Array.from(
                container.querySelectorAll<HTMLElement>("[data-time-point-label]"),
            );
            const rowRightEdges: number[] = [];
            const nextRows: number[] = [];
            const labelBounds = labelElements
                .map(labelElement => ({
                    index: Number(labelElement.dataset.timePointIndex),
                    bounds: labelElement.getBoundingClientRect(),
                }))
                .sort((first, second) => first.bounds.left - second.bounds.left);

            labelBounds.forEach(({ index, bounds: { left, right } }) => {
                const availableRow = rowRightEdges.findIndex(rowRight => left >= rowRight + 8);
                const row = availableRow === -1 ? rowRightEdges.length : availableRow;

                rowRightEdges[row] = right;
                nextRows[index] = row;
            });

            setLabelRows(currentRows =>
                currentRows.length === nextRows.length &&
                    currentRows.every((row, index) => row === nextRows[index])
                    ? currentRows
                    : nextRows,
            );
        };

        updateLabelRows();

        const resizeObserver = new ResizeObserver(updateLabelRows);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [selectedLabels]);

    const labelAreaHeight = Math.max(25, (Math.max(...labelRows, 0) + 1) * 24);

    const timelineOffset = labelAreaHeight - 25;

    return <div className="flex gap-5 pt-5 min-h-[72px]">
        <div
            className="flex flex-col items-center whitespace-nowrap min-w-[80px] relative"
            style={{ transform: `translateY(${timelineOffset}px)` }}
        >
            {selected === -1
                ? <>
                    <div>Arrived at site</div>
                    <div>{datetime(startOp.arrive_time || startOp.real_start_time).format(SHORT_DATETIME_FORMAT)}</div>
                </>
                : <div className="ml-[-55px] mt-[-12px] w-[135px] h-[calc(100%+12px)] flex flex-wrap gap-x-2 gap-y-[2px] items-center ">
                    {labels.map((item, i) => {
                        const isActive = selected === i;
                        return <div
                            key={i}
                            onClick={() => (isActive ? handleClick() : handleClick(i, item))}
                            className={`
                                group
                                flex items-center gap-[3px]
                                rounded-full px-2 py-0.5 cursor-pointer
                                border transition-all duration-200
                                ${isActive
                                    ? `
                                        bg-[--colorSecond]
                                        border-[--colorSecond]
                                        text-white
                                        hover:bg-transparent
                                        hover:text-[--colorMain]
                                    `
                                    : `
                                        border-transparent
                                        text-[--colorLink]
                                        hover:underline
                                    `
                                }
                            `}
                        >
                            <span>{item.name}</span>

                            {isActive && (
                                <Icon
                                    icon={ICON_NAME.CLOSE}
                                    className="icon bg-white transition-colors duration-200 group-hover:bg-[--colorMain]"
                                />
                            )}
                        </div>
                    })}
                </div>}
        </div>

        <div className="max-w-[calc(100%-152px)] translate-y-[-25px] w-full">
            <ScrollVertical>
                <div className="w-full relative" style={{ paddingTop: `${labelAreaHeight}px` }}>
                    {/* Labels */}
                    <div ref={labelContainerRef} className="absolute w-full top-0">
                        {selectedLabels.map((item, i) => (
                            <TimePoint
                                key={i}
                                data={item}
                                index={i}
                                row={labelRows[i] || 0}
                                onClick={item.steps ? () => handleClick(i, item) : undefined}
                            />
                        ))}
                    </div>

                    {/* Backgrounds */}
                    {selected === -1 && <div
                        className="absolute overflow-hidden w-full h-[50px]"
                        style={{ top: `${labelAreaHeight + 1}px` }}
                    >
                        {selectedLabels.map((item, i) => (
                            <TimePointBg key={i} data={item} />
                        ))}
                    </div>}
                    <div className={styles.ruler}>
                        <div className={`${styles.line} relative`}>
                            {data.map(time => {
                                const date = new Date(time);
                                const isHour = date.getMinutes() === 0;
                                const isLineLong = isHour && date.getHours() % 2 === 0;
                                const isSixHour = isHour && date.getHours() % 6 === 0;
                                const position = ((time - startTime) / (finishTime - startTime)) * 100;

                                return (
                                    <div
                                        className="absolute top-0"
                                        key={time}
                                        style={{ left: `${position}%` }}
                                    >
                                        <div
                                            className={`${styles.tick} ${isHour ? styles.hourTick : ""
                                                } ${isLineLong ? styles.bigTick : ""}`}
                                        />

                                        {isSixHour && (
                                            <div className="mt-[-22px] ml-[5px]">
                                                {datetime(time).format("HH:mm")}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </ScrollVertical>
        </div>

        <div
            className="flex flex-col items-center whitespace-nowrap ml-[20px] min-w-[72px]"
            style={{ transform: `translateY(${timelineOffset}px)` }}
        >
            <div>Departure</div>
            <div>{datetime(finishOp.depature_time || finishOp.real_start_time).format(SHORT_DATETIME_FORMAT)}</div>
        </div>
    </div>
}
